import React, { memo, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import './TaskList.scss'

// Status configuration - defined outside component to avoid recreation
const STATUS_CONFIG = {
	pending: { label: 'Pending', variant: 'warning' },
	pending_assignment: { label: 'Unassigned', variant: 'warning' },
	not_started: { label: 'Not Started', variant: 'default' },
	in_progress: { label: 'In Progress', variant: 'info' },
	pending_review: { label: 'Review', variant: 'primary' },
	approved: { label: 'Approved', variant: 'success' },
	completed: { label: 'Completed', variant: 'success' },
	rejected: { label: 'Rejected', variant: 'danger' },
	overdue: { label: 'Overdue', variant: 'danger' }
}

// Priority configuration - defined outside component
const PRIORITY_CONFIG = {
	high: 'danger',
	medium: 'warning',
	low: 'success'
}

/**
 * TaskList - List of tasks with status indicators
 * Memoized for performance optimization.
 *
 * @param {Array} tasks - Array of task objects
 * @param {string} title - List title
 * @param {number} maxItems - Maximum items to display
 * @param {boolean} showViewAll - Show "View All" link
 * @param {Function} onViewAll - View all callback
 * @param {Function} onTaskClick - Task click callback
 * @param {boolean} showCheckbox - Show checkboxes for completing tasks
 * @param {Function} onTaskComplete - Task complete callback
 * @param {boolean} loading - Show loading state
 */
const TaskList = memo(({
	tasks = [],
	title = 'My Tasks',
	maxItems = 5,
	showViewAll = false,
	onViewAll,
	onTaskClick,
	showCheckbox = false,
	onTaskComplete,
	loading = false
}) => {
	// Memoize status badge renderer
	const getStatusBadge = useCallback((status) => {
		const config = STATUS_CONFIG[status] || { label: status, variant: 'default' }
		return <span className={`task-list__status task-list__status--${config.variant}`}>{config.label}</span>
	}, [])

	// Memoize priority indicator
	const getPriorityIndicator = useCallback((priority) => {
		return PRIORITY_CONFIG[priority] || 'default'
	}, [])

	// Memoize displayed tasks
	const displayedTasks = useMemo(() =>
		tasks.slice(0, maxItems),
		[tasks, maxItems]
	)

	if (loading) {
		return (
			<div className="task-list">
				<div className="task-list__header">
					<h3 className="task-list__title">{title}</h3>
				</div>
				<div className="task-list__items">
					{[...Array(3)].map((_, i) => (
						<div key={i} className="task-list__item task-list__item--skeleton">
							<div className="skeleton skeleton--checkbox"></div>
							<div className="skeleton skeleton--content">
								<div className="skeleton skeleton--name"></div>
								<div className="skeleton skeleton--meta"></div>
							</div>
							<div className="skeleton skeleton--status"></div>
						</div>
					))}
				</div>
			</div>
		)
	}

	return (
		<div className="task-list">
			<div className="task-list__header">
				<h3 className="task-list__title">{title}</h3>
				{showViewAll && onViewAll && (
					<button className="task-list__view-all" onClick={onViewAll}>
						View All
					</button>
				)}
			</div>

			{displayedTasks.length === 0 ? (
				<div className="task-list__empty">
					<svg viewBox="0 0 24 24" fill="currentColor" className="task-list__empty-icon">
						<path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-9 14l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
					</svg>
					<p>No tasks to display</p>
				</div>
			) : (
				<div className="task-list__items">
					{displayedTasks.map((task, index) => (
						<div
							key={task.id || index}
							className={`task-list__item ${task.priority ? `task-list__item--priority-${getPriorityIndicator(task.priority)}` : ''}`}
							onClick={() => onTaskClick && onTaskClick(task)}
							role={onTaskClick ? 'button' : undefined}
							tabIndex={onTaskClick ? 0 : undefined}
						>
							{showCheckbox && (
								<label className="task-list__checkbox" onClick={(e) => e.stopPropagation()}>
									<input
										type="checkbox"
										checked={task.completed || task.status === 'completed'}
										onChange={() => onTaskComplete && onTaskComplete(task)}
									/>
									<span className="task-list__checkmark"></span>
								</label>
							)}

							<div className="task-list__content">
								<p className="task-list__name">{task.title || task.name}</p>
								<div className="task-list__meta">
									{task.project && (
										<span className="task-list__project">{task.project}</span>
									)}
									{task.due_date && (
										<span className="task-list__due-date">
											<svg viewBox="0 0 24 24" fill="currentColor">
												<path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11zM9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2z" />
											</svg>
											{task.due_date}
										</span>
									)}
									{task.assignee && (
										<span className="task-list__assignee">
											<svg viewBox="0 0 24 24" fill="currentColor">
												<path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
											</svg>
											{task.assignee}
										</span>
									)}
								</div>
							</div>

							{task.status && getStatusBadge(task.status)}
						</div>
					))}
				</div>
			)}
		</div>
	)
})

TaskList.displayName = 'TaskList'

TaskList.propTypes = {
	tasks: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			title: PropTypes.string,
			name: PropTypes.string,
			status: PropTypes.string,
			priority: PropTypes.oneOf(['high', 'medium', 'low']),
			due_date: PropTypes.string,
			project: PropTypes.string,
			assignee: PropTypes.string,
			completed: PropTypes.bool
		})
	),
	title: PropTypes.string,
	maxItems: PropTypes.number,
	showViewAll: PropTypes.bool,
	onViewAll: PropTypes.func,
	onTaskClick: PropTypes.func,
	showCheckbox: PropTypes.bool,
	onTaskComplete: PropTypes.func,
	loading: PropTypes.bool
}

export default TaskList
